import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { Icon, IconName } from './Icon';

interface TabsContextValue {
  selected?: string;
  select: (tabId: string) => void;
}

const TabsContext = createContext<TabsContextValue>({ select: () => undefined });

interface TabsProps {
  selected?: string;
  onChangeSelected?: (tabId: string) => void;
  children?: ReactNode;
}

export function Tabs({ selected, onChangeSelected, children }: TabsProps) {
  const [current, setCurrent] = useState(selected);

  useEffect(() => setCurrent(selected), [selected]);

  const select = (tabId: string) => {
    setCurrent(tabId);
    onChangeSelected?.(tabId);
  };

  return (
    <TabsContext.Provider value={{ selected: current, select }}>
      <div className="ec-tabs">{children}</div>
    </TabsContext.Provider>
  );
}

Tabs.Header = function TabsHeader({ testId, children }: { testId?: string; children?: ReactNode }) {
  return <div role="tablist" className="ec-tabs__nav" data-testid={testId}>{children}</div>;
};

interface HeaderItemProps {
  tabId: string;
  icon?: IconName;
  disabled?: boolean;
  className?: string;
  testId?: string;
  children?: ReactNode;
}

// The outer element carries role="presentation" and the testId, as Bronson's
// did; a test finds the tab labels that way.
Tabs.HeaderItem = function TabsHeaderItem({ tabId, icon, disabled, className, testId, children }: HeaderItemProps) {
  const { selected, select } = useContext(TabsContext);
  const active = !disabled && selected === tabId;

  return (
    <div role="presentation" className="ec-tabs__item" data-testid={testId}>
      <button
        id={tabId}
        type="button"
        role="tab"
        aria-selected={active}
        disabled={disabled}
        className={classNames('ec-tabs__link', active && 'is-active', className)}
        onClick={() => select(tabId)}
      >
        {icon ? <Icon name={icon} /> : null}
        <span className="ec-tabs__text">{children}</span>
      </button>
    </div>
  );
};

Tabs.Content = function TabsContent({ testId, children }: { testId?: string; children?: ReactNode }) {
  return <div className="ec-tabs__content" data-testid={testId}>{children}</div>;
};

interface ContentItemProps {
  tabId: string;
  testId?: string;
  className?: string;
  'aria-labelledby'?: string;
  children?: ReactNode;
}

// Every panel stays mounted and only the selected one is shown — the Bronson
// behaviour, which the tab components' data loading assumes.
Tabs.ContentItem = function TabsContentItem({ tabId, testId, className, children, ...otherProps }: ContentItemProps) {
  const { selected } = useContext(TabsContext);
  const active = selected === tabId;

  return (
    <div
      {...otherProps}
      role="tabpanel"
      data-testid={testId}
      aria-hidden={!active}
      hidden={!active}
      className={classNames('ec-tabs__panel', className)}
    >
      {children}
    </div>
  );
};
